// @flow

import castArray from 'lodash/castArray';
import compact from 'lodash/compact';
import { activate } from 'modules/rootEpic';
import { ActionsObservable } from 'redux-observable';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/filter';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { get, getSplitResults } from 'modules/participant/rest';
import identity from 'lodash/identity';
import isArray from 'lodash/isArray';
import type { Action } from 'type/type';
import ajax from 'services/ajax/ajax';
import { closePopup } from 'components/Popup/duck/duck';
import compose from 'lodash/fp/compose';
import prop from 'lodash/fp/prop';
import isEmpty from 'lodash/fp/isEmpty';
import map from 'lodash/fp/map';
import concat from 'lodash/fp/concat';
import { appendParam } from '@active/awe-ui-core/lib/services/url';
import {
  FETCH_PARTICIPANT,
  fetchParticipantDone,
  REPORT_ISSUE,
  fetchParticipantFailed,
  reportIssueFailure,
  reportIssueSuccess,
  fetchParticipantPhoto,
  FETCH_PARTICIPANT_PHOTO,
  FETCH_PARTICIPANT_DONE,
  updateParticipantPhotos,
  fetchParticipantPhotoFailed,
} from './duck';

export const fetchParticipantEpic = (actions$: ActionsObservable) => actions$.ofType(FETCH_PARTICIPANT)
  .mergeMap((action: Action) => forkJoin(
    get(action.payload.participantId, identity, fetchParticipantFailed),
    getSplitResults(action.payload.participantId, (...values) => castArray(values), fetchParticipantFailed),
  ))
  .filter(v => isArray(v))
  .map((values) => {
    const participant = values[0];
    const splitResults = compact(castArray(values[1])) || [];
    const photoURL = prop('participantCustomFields.photoURL')(participant);

    return fetchParticipantDone({
      ...participant,
      participantCustomFields: {
        ...participant.participantCustomFields,
        photoURL: {
          total: photoURL ? photoURL.length : 0,
          content: map(p => ({
            smallUrl: p,
            largeUrl: p,
          }))(photoURL),
        },
      },
      splitResults: (splitResults || []).map(sr => ({
        ...sr,
        participantId: values[0].id,
      })),
    });
  });

export const fetchParticipantDoneEpic = (action$: ActionsObservable) => action$.ofType(FETCH_PARTICIPANT_DONE)
  .filter((action: Action) => {
    const participantId = compose(prop('result'))(action.payload);
    const participant = compose(prop(participantId), prop('entities.participants'))(action.payload);
    const photos = prop('participantCustomFields.photoURL.content')(participant);
    return isEmpty(photos);
  })
  .map((action: Action) => {
    const participantId = compose(prop('result'))(action.payload);
    const participant = compose(prop(participantId), prop('entities.participants'))(action.payload);
    const externalId = prop('externalId')(participant);

    // If TPS does not have photo then we will call photo from EAS.
    return fetchParticipantPhoto(participantId, externalId);
  });

export const fetchParticipantPhotoEpic = (action$: ActionsObservable) => action$.ofType(FETCH_PARTICIPANT_PHOTO)
  .mergeMap((action: Action) => {
    const baseUrl = `/eventapp/photo/${action.payload.externalId}`;
    const { lastId } = action.payload;

    const config = {
      url: appendParam(baseUrl, 'lastId', lastId),
      headers: {
        accept: 'application/json',
      },
    };
    return ajax.get(config, {
      mapSuccess: (result) => {
        const apiStatus = prop('success')(result);
        if (apiStatus) {
          return updateParticipantPhotos({
            id: action.payload.participantId,
            participantCustomFields: {
              photoURL: {
                ...prop('data')(result),
                content: compose(concat(action.payload.prevPhotos || []), prop('data.content'))(result),
              },
            },
          });
        }

        return fetchParticipantPhotoFailed();
      },
      mapFailure: fetchParticipantPhotoFailed,
    });
  });

export const reportIssueEpic = (action$: ActionsObservable) => action$.ofType(REPORT_ISSUE)
  .mergeMap((action: Action) => ajax.post({
    url: '/results/issues',
    body: action.payload,
  }, {
    mapSuccess: () => [ closePopup(), reportIssueSuccess() ],
    mapFailure: reportIssueFailure,
  }));

activate(fetchParticipantEpic);
activate(reportIssueEpic);
activate(fetchParticipantDoneEpic);
activate(fetchParticipantPhotoEpic);
